import { useMyLeadsCRUDActions } from '@mm/state/actions/MyLeadsCRUDActions';
import {
  myLeadsExcludeTypes,
  MyLeadsFilterTypes,
  MyLeadsSearchQuery,
  MyLeadsUrlsQueryType,
  SubsectionKeys,
} from '@mm/state/interfaces/myLeads';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { Radio, RadioChangeEvent } from 'antd';
import { isObject } from 'lodash';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import CheckBox from '../../../../../../common/components/Checkbox';
import { LandingPagesRemoteSearch } from '../../../../../../common/components/LandingPagesRemoteSearch/LandingPagesRemoteSearch';
import { convertToUpperSnakeCase } from '../../GetLeads/LeadsSearch/LeadsSearchFiltersMenu/helpers';
import { useFilterParams } from '../MyLeadsFiltersPanel/use-filter-params';
import styles from './FilterByLandingPage.module.css';

type Props = {
  type: 'variables' | 'attributes';
  title: string;
  attributeKey: string;
  isLowercasedKey?: boolean;
  withAndOperator?: boolean;
  filterParams: MyLeadsSearchQuery;
  setFilterParams: (params: MyLeadsSearchQuery) => void;
};

export const FilterByLandingPage = ({
  type,
  title,
  attributeKey,
  isLowercasedKey,
  withAndOperator,
  filterParams,
  setFilterParams,
}: Props) => {
  const [operator, setOperator] = useState<undefined | 'and'>(undefined);
  const botId = useSelector(activeChatbotIdSelector);
  const { retrieveLandingPages, retrieveVisitedUrls } = useMyLeadsCRUDActions();
  const { getUpdatedFilterParams, shouldDisableFilters, isFilterParamExist } =
    useFilterParams({
      filterParams,
    });

  const sectionKey = isLowercasedKey
    ? attributeKey
    : convertToUpperSnakeCase(attributeKey);

  const [showExluded, setShowExcluded] = useState(false);

  const onFetch = async (query: string, queryType: MyLeadsUrlsQueryType) => {
    if (query?.length >= 3) {
      switch (attributeKey) {
        case SubsectionKeys.landingPageUrl:
          return retrieveLandingPages(botId, query, queryType);
        case SubsectionKeys.urlsVisited:
          return retrieveVisitedUrls(botId, query, queryType);
        default:
          return;
      }
    }
    return [];
  };

  const onUpdateFilterParams = (value: string, type: MyLeadsFilterTypes) => {
    const currentValue: [{ [key: string]: string[] }] | string[] | undefined =
      filterParams.query?.[type]?.[sectionKey];
    const isObjectValue = isObject(currentValue?.[0]);

    const valuesArr: string[] = isObjectValue
      ? Object.values(currentValue?.[0] || {}).flat()
      : currentValue || [];

    const isExist = isFilterParamExist({ key: sectionKey, value, type });

    if (currentValue) {
      delete filterParams.query?.[type]?.[sectionKey];
    }

    let updatedValue;
    if (operator) {
      updatedValue = !isExist
        ? { [operator]: [...valuesArr, value] }
        : { [operator]: valuesArr.filter(v => v !== value) };
    } else {
      updatedValue = !isExist
        ? [...valuesArr, value]
        : valuesArr.filter(v => v !== value);
    }

    const updatedQuery = getUpdatedFilterParams({
      key: sectionKey,
      value: updatedValue,
      isExisting: false,
      type,
    });
    setFilterParams(updatedQuery);
  };

  const hasExcludedFilters = Object.keys(filterParams.query || {}).some(
    key => myLeadsExcludeTypes[key],
  );

  const onRadioChange = (e: RadioChangeEvent) => {
    const { value } = e.target || {};
    setOperator(value);
  };

  return (
    <div className={styles.remoteSearchContainer}>
      <div className={styles.title}>{title}</div>
      {withAndOperator && (
        <Radio.Group onChange={onRadioChange} value={operator}>
          <div className={styles.radioContainer}>
            <Radio
              defaultChecked={!operator}
              value={undefined}
              className={styles.radio}
              disabled={shouldDisableFilters}
            >
              At least one condition
            </Radio>
            <Radio
              defaultChecked={operator === 'and'}
              value="and"
              className={styles.radio}
              disabled={shouldDisableFilters}
            >
              Every condition
            </Radio>
          </div>
        </Radio.Group>
      )}
      <LandingPagesRemoteSearch
        title="Include URL"
        filterParams={filterParams}
        onFetch={onFetch}
        onUpdate={onUpdateFilterParams}
        searchDisabled={shouldDisableFilters}
        css={{ title: styles.selectTitle }}
        sectionKey={sectionKey}
        type={type}
        logicOperator={operator}
      />
      <div className={styles.excludedContainer}>
        <CheckBox
          label="Exclude URL"
          isChecked={showExluded}
          onClick={() => {
            if (hasExcludedFilters) return;
            setShowExcluded(!showExluded);
          }}
          className={styles.excludedCheckbox}
        />
        {showExluded && (
          <LandingPagesRemoteSearch
            title="Exclude URL"
            filterParams={filterParams}
            onFetch={onFetch}
            onUpdate={onUpdateFilterParams}
            searchDisabled={shouldDisableFilters}
            css={{ title: styles.selectTitle }}
            sectionKey={sectionKey}
            type={type}
            logicOperator={operator}
            exlude
          />
        )}
      </div>
    </div>
  );
};
